import fastdom from 'fastdom';

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

var objectCounter = 0;
var objectWeakMap = /*#__PURE__*/new WeakMap();
var instanceMap = {};
var getNode = function getNode(dep) {
  return dep instanceof Node ? dep : null;
};
var getInstance = function getInstance(dep) {
  return dep instanceof Instance ? dep : null;
};

var isObject = function isObject(dep) {
  return dep instanceof Object || typeof dep === 'function';
};

var Node = function Node(func, deps, type) {
  this.func = func;
  this.type = type;
  this.deps = deps;
};
var Instance = /*#__PURE__*/function (_Node) {
  _inheritsLoose(Instance, _Node);

  function Instance(id, func, deps, type) {
    var _this;

    _this = _Node.call(this, func, deps, type) || this;
    _this.id = id;
    _this.args = [];
    _this.value = undefined;
    _this.refs = 0;
    return _this;
  }

  var _proto = Instance.prototype;

  _proto.ref = function ref(_) {
    this.refs++;
  };

  _proto.unref = function unref(_) {
    --this.refs;

    if (this.refs <= 0) {
      delete instanceMap[this.id];
    }
  };

  _proto.resolve = function resolve() {
    var depCount = this.deps.length;
    var same = this.value !== undefined;

    for (var i = 0, dep; i < depCount; i++) {
      dep = this.deps[i];

      var arg = _resolve(dep);

      same = same && arg === this.args[i];
      this.args[i] = arg;
    }

    if (same) {
      return this.value;
    }

    this.value = this.func.apply(this, this.args);
    return this.value;
  };

  _proto.debug = function debug(collector) {
    collector[this.id] = this;
    var depCount = this.deps.length;

    for (var i = 0, instance; i < depCount; i++) {
      instance = getInstance(this.deps[i]);

      if (instance) {
        instance.debug(collector);
      }
    }

    return collector;
  };

  return Instance;
}(Node);

var EventInstance = /*#__PURE__*/function (_Instance) {
  _inheritsLoose(EventInstance, _Instance);

  function EventInstance() {
    var _this2;

    _this2 = _Instance.apply(this, arguments) || this;
    _this2.observers = [];
    _this2.value = 0;

    _this2.onChange = function () {
      _this2.value++;

      for (var i = 0, observer; observer = _this2.observers[i]; i++) {
        observer.triggerResolve();
      }
    };

    return _this2;
  }

  var _proto2 = EventInstance.prototype;

  _proto2.ref = function ref(observer) {
    _Instance.prototype.ref.call(this, observer);

    if (observer) {
      this.observers.push(observer);
    }

    if (this.refs === 1) {
      this.unsubscribe = this.func.apply(this, [this.onChange].concat(this.deps));
    }
  };

  _proto2.unref = function unref(observer) {
    _Instance.prototype.unref.call(this, observer);

    if (observer) {
      var index = this.observers.indexOf(observer);
      this.observers.splice(index, 1);
    }

    if (this.refs === 0 && this.unsubscribe) {
      this.unsubscribe();
    }
  };

  _proto2.resolve = function resolve() {
    // no-op. value is updated by event handler.
    return this.value;
  };

  return EventInstance;
}(Instance); // Updates a dependency and returns it's new value.


var _resolve = function _resolve(dep) {
  var instance = getInstance(dep);

  if (!instance) {
    return dep;
  }

  return instance.resolve();
};

var lookup = function lookup(val) {
  // Special case undefined.
  if (val === undefined) {
    return 'undefined';
  } // Other primitives, null, boolean, number, string.


  if (!isObject(val)) {
    return JSON.stringify(val);
  } // Use ids and a WeakMap for Object, Arrays and Functions.
  // Do we already recognize this object?


  var id = objectWeakMap.get(val);

  if (id) {
    return id;
  } // Create an id for it.


  id = "#" + objectCounter++;

  if (process.env.NODE_ENV === 'development') {
    var friendlyName = val.constructor.name;

    if (typeof val === 'function' && val.name) {
      friendlyName = val.name;
    }

    if (val instanceof Instance && val.func.name) {
      friendlyName = "Instance:" + val.func.name;
    }

    id = "" + friendlyName + id;
  }

  objectWeakMap.set(val, id);
  return id;
};

var createKey = function createKey(node, deps) {
  var func = lookup(node.func);
  var depString = deps.map(function (dep) {
    return lookup(dep);
  }).join(', ');
  return func + "(" + depString + ")";
};

var factory = function factory(node, deps) {
  var id = createKey(node, deps);
  var instance = instanceMap[id];

  if (instance) {
    return instance;
  }

  switch (node.type) {
    case 'event':
      instance = new EventInstance(id, node.func, deps, node.type);
      break;

    default:
      instance = new Instance(id, node.func, deps, node.type);
      break;
  }

  instanceMap[id] = instance;
  return instance;
}; // Updates a dependency. If newDep is a node, return a singleton node instance
// based on its dependencies.


var update = function update(newDep, oldDep, observer) {
  // Dep can be any value as well as a node. Node is a stricter structure.
  var oldInstance = getInstance(oldDep);
  var newNode = getNode(newDep);
  var isContextNode = newNode && newNode.type === 'context';

  if (!newNode || isContextNode) {
    if (oldInstance) {
      oldInstance.deps.forEach(function (dep) {
        return update(undefined, dep, observer);
      });
      oldInstance.unref(observer);
    }

    return newNode && isContextNode ? newNode.func.apply(newNode, [observer && observer.context].concat(newNode.deps)) : newDep;
  } // Track if newNode differs in any way from oldNode.


  var same = oldInstance && oldInstance.func === newNode.func;
  var deps = [];
  var index = 0; // Update all deps, depth first.

  while (index < newNode.deps.length) {
    var dep = update(newNode.deps[index], oldInstance && oldInstance.deps[index], observer); // Did dep change?

    same = same && oldInstance && oldInstance.deps[index] !== dep;
    deps.push(dep);
    index++;
  } // If oldNode had more deps, then nodes are not the same. Unref oldNode.


  if (oldInstance && oldInstance.deps.length > index) {
    same = false;
  } // If nodes are same, return existing singleton node.


  if (same) {
    return oldInstance;
  } // Find or create singleton node based on deps.


  var newInstance = factory(newNode, deps);
  newInstance.ref(observer); // Clean up the existing instance.

  if (oldInstance) {
    oldInstance.unref(observer);
  }

  return newInstance;
};

var eventHandler = function eventHandler(onChange, event) {
  window.addEventListener(event, onChange);
  return function () {
    window.removeEventListener(event, onChange);
  };
};

var rafHandler = function rafHandler(onChange) {
  var unsubscribed = false;
  fastdom.measure(frame);

  function frame() {
    // Stop if unsubscribed.
    if (unsubscribed) {
      return;
    } // To schedule a measure in the next frame, we need to be
    // in the mutation phase.


    fastdom.mutate(request); // Invalidate subscribed observers.

    onChange();
  }

  function request() {
    fastdom.measure(frame);
  }

  return function () {
    unsubscribed = true;
  };
};

var scrollYHandler = function scrollYHandler() {
  return window.scrollY;
};

var scrollXHandler = function scrollXHandler() {
  return window.scrollX;
};

var viewportHeightHandler = function viewportHeightHandler() {
  return window.innerHeight;
};

var viewportWidthHandler = function viewportWidthHandler() {
  return window.innerWidth;
};

var elementWidthHandler = function elementWidthHandler(element) {
  return element.offsetWidth;
};

var elementHeightHandler = function elementHeightHandler(element) {
  return element.offsetHeight;
};

var viewportRatioHandler = function viewportRatioHandler(ratio, scroll, size) {
  return scroll + ratio * size;
};

var elementXHandler = function elementXHandler(ratio, offset, size) {
  return offset.left + ratio * size;
};

var elementYHandler = function elementYHandler(ratio, offset, size) {
  return offset.top + ratio * size;
};

var rootElementHandler = function rootElementHandler(context) {
  return context.rootElement;
};

var elementHandler = function elementHandler(rootElement, selector) {
  return rootElement.querySelector(selector);
};

var elementOffsetHandler = function elementOffsetHandler(element) {
  var x = 0;
  var y = 0;

  while (element && !isNaN(element.offsetLeft) && !isNaN(element.offsetTop)) {
    x += element.offsetLeft + element.clientLeft;
    y += element.offsetTop + element.clientTop;

    if (element === document.body) {
      x -= window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0;
      y -= window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    } else {
      x -= element.scrollLeft;
      y -= element.scrollTop;
    }

    element = element.offsetParent;
  }

  return {
    top: y + window.pageYOffset,
    left: x + window.pageXOffset
  };
};

var transformedBoundsHandler = function transformedBoundsHandler(element) {
  var rect = element.getBoundingClientRect();
  return {
    x: rect.left + window.scrollX,
    y: rect.top + window.scrollY,
    width: rect.width,
    height: rect.height
  };
};

var createNodeFactory = function createNodeFactory(type) {
  return function () {
    for (var _len = arguments.length, deps = new Array(_len), _key = 0; _key < _len; _key++) {
      deps[_key] = arguments[_key];
    }

    var func = deps.pop();
    return new Node(func, deps, type);
  };
};

var subscribe = /*#__PURE__*/createNodeFactory('event');
var context = /*#__PURE__*/createNodeFactory('context');
var observe = /*#__PURE__*/createNodeFactory('observe');
var resizeEvent = function resizeEvent() {
  return subscribe('resize', eventHandler);
};
var scrollEvent = function scrollEvent() {
  return subscribe('scroll', eventHandler);
};
var rafEvent = function rafEvent() {
  return subscribe(rafHandler);
};
var scrollY = function scrollY() {
  return observe(scrollEvent(), scrollYHandler);
};
var scrollX = function scrollX() {
  return observe(scrollEvent(), scrollXHandler);
};
var viewportHeight = function viewportHeight() {
  return observe(resizeEvent(), viewportHeightHandler);
};
var viewportWidth = function viewportWidth() {
  return observe(resizeEvent(), viewportWidthHandler);
};
var viewportY = function viewportY(ratio) {
  if (ratio === void 0) {
    ratio = 0;
  }

  return ratio ? observe(ratio, scrollY(), viewportHeight(), viewportRatioHandler) : scrollY();
};
var viewportX = function viewportX(ratio) {
  if (ratio === void 0) {
    ratio = 0;
  }

  return ratio ? observe(ratio, scrollX(), viewportWidth(), viewportRatioHandler) : scrollX();
};
var rootElement = function rootElement() {
  return context(rootElementHandler);
};
var element = function element(selector, deps) {
  if (selector === void 0) {
    selector = null;
  }

  if (deps === void 0) {
    deps = [];
  }

  return selector ? observe.apply(void 0, [rootElement(), selector].concat(deps, [elementHandler])) : rootElement();
};
var elementOffset = function elementOffset(element, deps) {
  if (element === void 0) {
    element = rootElement();
  }

  if (deps === void 0) {
    deps = [resizeEvent()];
  }

  return observe.apply(void 0, [element].concat(deps, [elementOffsetHandler]));
};
var elementHeight = function elementHeight(element, deps) {
  if (element === void 0) {
    element = rootElement();
  }

  if (deps === void 0) {
    deps = [resizeEvent()];
  }

  return observe.apply(void 0, [element].concat(deps, [elementHeightHandler]));
};
var elementWidth = function elementWidth(element, deps) {
  if (element === void 0) {
    element = rootElement();
  }

  if (deps === void 0) {
    deps = [resizeEvent()];
  }

  return observe.apply(void 0, [element].concat(deps, [elementWidthHandler]));
};
var elementY = function elementY(ratio, element, deps) {
  if (ratio === void 0) {
    ratio = 0;
  }

  if (element === void 0) {
    element = rootElement();
  }

  if (deps === void 0) {
    deps = [resizeEvent()];
  }

  return observe(ratio, elementOffset(element, deps), elementHeight(element, deps), elementYHandler);
};
var elementX = function elementX(ratio, element, deps) {
  if (ratio === void 0) {
    ratio = 0;
  }

  if (element === void 0) {
    element = rootElement();
  }

  if (deps === void 0) {
    deps = [resizeEvent()];
  }

  return observe(ratio, elementOffset(element, deps), elementWidth(element, deps), elementXHandler);
};
var transformedBounds = function transformedBounds(element, deps) {
  if (element === void 0) {
    element = rootElement();
  }

  if (deps === void 0) {
    deps = [resizeEvent()];
  }

  return observe.apply(void 0, [element].concat(deps, [transformedBoundsHandler]));
};
var getElementOffset = elementOffsetHandler;

var Observer = /*#__PURE__*/function () {
  function Observer(value, options) {
    var _this = this;

    if (options === void 0) {
      options = {};
    }

    this.triggerChange = function () {
      if (_this.onChange) {
        _this.onChange(_this.value);
      }
    };

    this.resolve = function () {
      _this.queued = false;
      var oldValue = _this.value;
      _this.value = _resolve(_this.root);

      if (oldValue !== _this.value) {
        _this.printDebug();

        fastdom.mutate(_this.triggerChange);
      }
    };

    this.triggerResolve = function () {
      if (_this.queued) {
        return;
      }

      _this.queued = true;
      fastdom.measure(_this.resolve);
    };

    this.onChange = options.onChange;
    this.root = null;
    this.queued = false;
    this.value = undefined;
    this.context = options.context || {};
    this.debug = options.debug || false;
    this.update(value);
  }

  var _proto = Observer.prototype;

  _proto.dispose = function dispose() {
    this.onChange = undefined;
    this.update(undefined);
  };

  _proto.printDebug = function printDebug() {
    if (this.debug) {
      var instance = getInstance(this.root);

      if (instance) {
        var table = instance.debug({}); // tslint:disable-next-line:no-console

        console.table(table, ['value']);
      }
    }
  };

  _proto.update = function update$1(value) {
    var oldValue = this.value;
    this.root = update(value, this.root, this);
    this.value = _resolve(this.root);
    this.printDebug();

    if (this.onChange && this.value !== oldValue) {
      this.onChange(this.value);
    }
  };

  return Observer;
}();

export default Observer;
export { Node, context, element, elementHeight, elementOffset, elementWidth, elementX, elementY, getElementOffset, observe, rafEvent, resizeEvent, rootElement, scrollEvent, scrollX, scrollY, subscribe, transformedBounds, viewportHeight, viewportWidth, viewportX, viewportY };
//# sourceMappingURL=ui-observer.esm.js.map
